import type { ReactNode } from 'react'
import { toastManager } from '@systemeio/components'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import ConfirmationModal from 'shared/components/confirmation-modal'
import Pagination from 'shared/components/pagination'
import type { PaginationData } from 'shared/components/pagination'
import useUser from 'shared/hooks/use-user'
import { useBannedMembers } from 'modules/community/api/get-banned-members'
import { communityKeys } from 'modules/community/api/key-factory'
import * as postsApi from 'modules/community/api/posts-api'
import { MemberShort } from 'modules/community/components/member/member-short/member-short'
import { SkeletonMemberShort } from 'modules/community/components/member/member-short/member-short-skeleton'
import { MembersEmptyList } from 'modules/community/components/member/members-empty-list'
import type { BannedMemberInterface } from 'modules/community/types/member-interface'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'

interface BannedTabContentProps {
  communityPath: string
  pageIndex: number
  setPageIndex: (page: number) => void
  searchQuery: string
  unbanId: number | null
  setUnbanId: (id: number | null) => void
}

export const BannedTabContent = ({
  communityPath,
  pageIndex,
  setPageIndex,
  searchQuery,
  unbanId,
  setUnbanId,
}: BannedTabContentProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const queryClient = useQueryClient()

  const { data, isPending } = useBannedMembers({
    communityPath,
    page: pageIndex,
    searchBy: searchQuery,
  })

  const unbanMember = async (): Promise<void> => {
    // TODO: rewrite to use TanStack Query
    try {
      if (!unbanId) return
      await postsApi.unbanUser(unbanId, communityPath)
      queryClient.setQueriesData<PaginationData<BannedMemberInterface[]>>(
        { queryKey: communityKeys.bannedMembers.lists() },
        old => {
          if (!old) return old
          const data = old.data.filter(member => member.userId !== unbanId)
          if (data.length === old.data.length) return old
          return { total: Math.max(0, old.total - 1), data }
        },
      )
      void queryClient.invalidateQueries({ queryKey: communityKeys.bannedMembers.all })
      toastManager.add({ type: 'success', title: t('members.unban_success_toast') })
      setUnbanId(null)
    } catch (error) {
      toastManager.add({ type: 'error', title: t('members.unban_failed_toast') })
      throw error
    }
  }

  if (isPending) {
    return (
      <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
        {[...Array.from({ length: 9 }).keys()].map(index => (
          <SkeletonMemberShort key={index} />
        ))}
      </div>
    )
  }

  if (!data) {
    return null
  }

  const { data: members, total } = data

  if (total === 0) {
    return (
      <MembersEmptyList
        title={t('global.list.empty_title')}
        description={t('members_banned.empty.state.description')}
        small
      />
    )
  }

  return (
    <>
      {members.length > 0 && (
        <div className="mb-6 grid w-full grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
          {members.map(member => (
            <MemberShort
              key={member.id}
              member={member}
              actionMenuItems={[
                hasPermission(user, 'members', 'unban', { member }) && {
                  label: t('members_ban.actions.unban.label'),
                  onClick: () => {
                    setUnbanId(member.userId)
                  },
                },
              ].filter(Boolean)}
            />
          ))}
        </div>
      )}

      {total > 0 && (
        <div className="flex justify-center rounded-lg bg-bg-primary p-3">
          <Pagination
            total={total}
            currentPage={pageIndex}
            onChangePage={setPageIndex}
            pageSize={9}
          />
        </div>
      )}

      {!!unbanId && (
        <ConfirmationModal
          onConfirm={unbanMember}
          onCloseModal={() => {
            setUnbanId(null)
          }}
          confirmationContent={t('settings.form.unban_user_confirmation.label')}
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_unbanned',
          }}
        />
      )}
    </>
  )
}
